export const DRUM_ROWS = 5;
export const TAP_TARGET_PX = 96;
export const MIN_BPM = 60;
export const MAX_BPM = 180;
export const DEFAULT_BPM = 100;

/** Base note value for a single grid cell. */
export type SubdivisionBase = "quarter" | "eighth" | "sixteenth";

export interface SubdivisionFlags {
  base: SubdivisionBase;
  triplet: boolean;
}

export const DEFAULT_SUBDIVISION_FLAGS: SubdivisionFlags = {
  base: "eighth",
  triplet: false,
};

export const SUBDIVISION_BASES: readonly SubdivisionBase[] = [
  "quarter",
  "eighth",
  "sixteenth",
];

function resolveBase(flags: SubdivisionFlags | undefined): SubdivisionBase {
  const base = flags?.base;
  if (base === "quarter" || base === "eighth" || base === "sixteenth")
    return base;
  return "eighth";
}

function resolveTriplet(flags: SubdivisionFlags | undefined): boolean {
  return flags?.triplet === true;
}

/** How many grid cells fit in one 4/4 bar for the given subdivision. */
export function stepsPerBar(flags: SubdivisionFlags | undefined): number {
  const base = resolveBase(flags);
  const count = base === "quarter" ? 4 : base === "eighth" ? 8 : 16;
  return resolveTriplet(flags) ? (count * 3) / 2 : count;
}

/** Seconds per cell for a given subdivision at the supplied BPM. */
export function stepSeconds(
  flags: SubdivisionFlags | undefined,
  bpm: number,
): number {
  const barSeconds = 240 / bpm;
  return barSeconds / stepsPerBar(flags);
}

/** Tone.Time value (e.g. '8n', '16t') for the subdivision. */
export function toneInterval(flags: SubdivisionFlags | undefined): string {
  const base = resolveBase(flags);
  const n = base === "quarter" ? "4" : base === "eighth" ? "8" : "16";
  return resolveTriplet(flags) ? `${n}t` : `${n}n`;
}

/** Cycle the base subdivision: quarter → eighth → sixteenth → quarter. */
export function cycleSubdivisionBase(
  current: SubdivisionBase,
): SubdivisionBase {
  const idx = SUBDIVISION_BASES.indexOf(current);
  return SUBDIVISION_BASES[(idx + 1) % SUBDIVISION_BASES.length];
}

export function baseLabel(base: SubdivisionBase): string {
  if (base === "quarter") return "1/4";
  if (base === "eighth") return "1/8";
  return "1/16";
}

export function baseGlyph(base: SubdivisionBase): string {
  if (base === "quarter") return "♩";
  if (base === "eighth") return "♪";
  return "♬";
}
